// src/pages/YahooSettingsPage.tsx
//
// Yahoo!ショッピング（ストアクリエイターPro）連携設定ページ。
// 店舗ID・ログインID・パスワードを登録する。
// Yahoo は CAPTCHA で全自動ログインを弾くため、ここではライブ接続チェックはせず、
// 店舗登録＋資格情報の保存だけをおこなう（初回セッション確立は運用側でブートストラップ）。
//
// UI はデジタル庁デザインシステム系の手順（ステップ）UI。
// - 各ステップはアコーディオン。見出しの下にステータスを出す
// - 店舗情報・ログイン情報の入力はポップアップ（モーダル）で受け取り、モーダルの「保存する」が
//   その場でサーバー登録（API送信）する。「入力」と「登録」を分けないので迷わない
//   ※資格情報は店舗登録後でないと弾かれるため、手順2（店舗）→手順3（ログイン）の順で保存する
// - 色は使いすぎず、構造はグレー系。アクセントはプライマリー（blue-600）1色、意味色は成功=緑・エラー=赤だけ
// - a11y 優先：label と入力の紐付け、aria-expanded/controls、role=dialog、フォーカストラップ、focus-visible

import React, { useEffect, useRef, useState } from "react";
import { useMallContext } from "../contexts/MallContext";
import { shopRefFromStoreId } from "../types/mall";
import { apiDeleteJson, apiGetJson, apiPostJson } from "../utils/apiClient";

const YAHOO_SECURITY_URL = "https://accounts.yahoo.co.jp/profile?current=security";

const inputClass =
    "mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-500/30";

const primaryBtn =
    "inline-flex items-center justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-40";

// モーダル・ステップの操作ボタンは中央ぞろえで、すこし幅を広く取る。どちらも塗りつぶし
const modalPrimaryBtn = primaryBtn + " min-w-[8rem]";
const stepActionBtn = primaryBtn + " min-w-[12rem]";

// キャンセルは枠線なしのテキスト風。ただし px/py・min-w はそのままで、おせる範囲は変えない
const modalCancelBtn =
    "inline-flex items-center justify-center rounded-lg px-4 py-2 min-w-[8rem] text-sm font-medium text-blue-600 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-40";

// ── ステータス表示（見出しの下に置く小さな1行） ──────────────
function StatusLine({ done, children }: { done?: boolean; children: React.ReactNode }) {
    return (
        <span
            className={[
                "inline-flex items-center gap-1.5 text-xs",
                done ? "text-emerald-700" : "text-slate-500",
            ].join(" ")}
        >
            <span
                aria-hidden="true"
                className={["h-1.5 w-1.5 rounded-full", done ? "bg-emerald-500" : "bg-slate-300"].join(" ")}
            />
            {children}
        </span>
    );
}

// ── 入力フィールド（label↔input を id で紐付け、補足は aria-describedby） ──
function Field({
    id,
    label,
    required = false,
    optional = false,
    hint,
    children,
}: {
    id: string;
    label: string;
    required?: boolean;
    optional?: boolean;
    hint?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-slate-800">
                {label}
                {required && (
                    <>
                        <span aria-hidden="true" className="ml-0.5 text-slate-900">
                            *
                        </span>
                        <span className="sr-only">（必須）</span>
                    </>
                )}
                {optional && <span className="ml-1.5 text-xs font-normal text-slate-400">（任意）</span>}
            </label>
            {children}
            {hint && (
                <p id={`${id}-hint`} className="mt-1.5 text-xs leading-relaxed text-slate-500">
                    {hint}
                </p>
            )}
        </div>
    );
}

// ── アコーディオン1ステップ ─────────────────────────────
function StepAccordion({
    n,
    title,
    status,
    open,
    onToggle,
    children,
}: {
    n: number;
    title: string;
    status: React.ReactNode;
    open: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) {
    const bodyId = `ysc-step-${n}-body`;
    const headerId = `ysc-step-${n}-header`;
    return (
        <li className="border-b border-slate-200 last:border-b-0">
            <h2>
                <button
                    id={headerId}
                    type="button"
                    aria-expanded={open}
                    aria-controls={bodyId}
                    onClick={onToggle}
                    className="flex w-full items-center gap-4 rounded-lg py-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                >
                    <span
                        aria-hidden="true"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white"
                    >
                        {n}
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block text-base font-semibold text-slate-900">{title}</span>
                        <span className="mt-1 block">{status}</span>
                    </span>
                    <svg
                        aria-hidden="true"
                        viewBox="0 0 20 20"
                        fill="none"
                        className={["h-5 w-5 shrink-0 text-slate-400 transition-transform", open ? "rotate-180" : ""].join(
                            " ",
                        )}
                    >
                        <path
                            d="M6 8l4 4 4-4"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </h2>
            <div id={bodyId} role="region" aria-labelledby={headerId} hidden={!open} className="pb-6 pl-12 pr-1">
                {children}
            </div>
        </li>
    );
}

// ── モーダル（フォーカストラップ・Escape・背景クリックで閉じる） ──
function Modal({
    open,
    titleId,
    title,
    onClose,
    children,
}: {
    open: boolean;
    titleId: string;
    title: string;
    onClose: () => void;
    children: React.ReactNode;
}) {
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const prevActive = document.activeElement as HTMLElement | null;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const focusables = () =>
            Array.from(
                panelRef.current?.querySelectorAll<HTMLElement>(
                    'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])',
                ) ?? [],
            );

        focusables()[0]?.focus();

        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
                return;
            }
            if (e.key === "Tab") {
                const f = focusables();
                if (f.length === 0) return;
                const first = f[0];
                const last = f[f.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
            prevActive?.focus?.();
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/40" aria-hidden="true" onClick={onClose} />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl"
            >
                <div className="flex items-start justify-between gap-4">
                    <h2 id={titleId} className="text-base font-semibold text-slate-900">
                        {title}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="とじる"
                        className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    >
                        <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5" fill="none">
                            <path
                                d="M5 5l10 10M15 5L5 15"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>
                <div className="mt-5">{children}</div>
            </div>
        </div>
    );
}

// ── 店舗情報モーダル（保存でその場サーバー登録） ───────────────
function StoreModal({
    open,
    initialStoreId,
    initialStoreName,
    onClose,
    onSaved,
}: {
    open: boolean;
    initialStoreId: string;
    initialStoreName: string;
    onClose: () => void;
    onSaved: (storeId: string, storeName: string) => void;
}) {
    const [storeId, setStoreId] = useState(initialStoreId);
    const [storeName, setStoreName] = useState(initialStoreName);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setStoreId(initialStoreId);
            setStoreName(initialStoreName);
            setSaving(false);
            setError(null);
        }
    }, [open, initialStoreId, initialStoreName]);

    const submit = async () => {
        const s = storeId.trim();
        if (!s) {
            setError("店舗ID（ストアアカウント）を入力してください");
            return;
        }
        const name = storeName.trim() || s;
        setSaving(true);
        setError(null);
        try {
            await apiPostJson("/api/company/shops", { mall: "yahoo", mallShopId: s, displayName: name });
            onSaved(s, name);
        } catch (e) {
            setError("登録できませんでした。入力した内容を確かめて、もう一度おためしください。");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} titleId="ysc-store-modal-title" title="店舗情報を登録" onClose={onClose}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    void submit();
                }}
                className="space-y-4"
            >
                <Field
                    id="sm-store-id"
                    label="店舗ID（ストアアカウント）"
                    required
                    hint={
                        <>
                            ストアクリエイターProのURL「pro.store.yahoo.co.jp/pro.
                            <b className="text-slate-600">ストアアカウント</b>」の太字のところです
                        </>
                    }
                >
                    <input
                        id="sm-store-id"
                        type="text"
                        value={storeId}
                        onChange={(e) => setStoreId(e.target.value)}
                        placeholder="例: your-store"
                        autoComplete="off"
                        aria-required="true"
                        aria-describedby="sm-store-id-hint"
                        className={inputClass}
                    />
                </Field>

                <Field id="sm-store-name" label="店舗表示名" optional>
                    <input
                        id="sm-store-name"
                        type="text"
                        value={storeName}
                        onChange={(e) => setStoreName(e.target.value)}
                        placeholder="例: Yahoo本店"
                        className={inputClass}
                    />
                </Field>

                {error && (
                    <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
                        {error}
                    </p>
                )}

                <div className="flex justify-center gap-3 pt-1">
                    <button type="button" onClick={onClose} className={modalCancelBtn} disabled={saving}>
                        キャンセル
                    </button>
                    <button type="submit" className={modalPrimaryBtn} disabled={saving}>
                        {saving ? "登録中…" : "登録する"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// ── ログイン情報モーダル（保存でその場サーバー登録・パスワードは prefill しない） ──
function LoginModal({
    open,
    initialLoginId,
    onClose,
    onSaved,
}: {
    open: boolean;
    initialLoginId: string;
    onClose: () => void;
    onSaved: (loginId: string) => void;
}) {
    const [loginId, setLoginId] = useState(initialLoginId);
    const [password, setPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setLoginId(initialLoginId);
            setPassword("");
            setSaving(false);
            setError(null);
        }
    }, [open, initialLoginId]);

    const submit = async () => {
        const l = loginId.trim();
        if (!l || !password) {
            setError("ログインID・パスワードを入力してください");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await apiPostJson("/api/yahoo/credentials", { loginId: l, password });
            setPassword(""); // 成功後は平文パスワードを state に残さない
            onSaved(l);
        } catch (e) {
            setError("登録できませんでした。入力した内容を確かめて、もう一度おためしください。");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} titleId="ysc-login-modal-title" title="ログイン情報を登録" onClose={onClose}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    void submit();
                }}
                className="space-y-4"
            >
                <Field id="lm-login-id" label="ログインID（Yahoo! JAPAN ID）" required>
                    <input
                        id="lm-login-id"
                        type="text"
                        value={loginId}
                        onChange={(e) => setLoginId(e.target.value)}
                        placeholder="Yahoo! JAPAN ID"
                        autoComplete="off"
                        aria-required="true"
                        className={inputClass}
                    />
                </Field>

                <Field
                    id="lm-password"
                    label="パスワード"
                    required
                    hint="データの取りこみのためだけに使い、暗号化して大切に保管します"
                >
                    <input
                        id="lm-password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="パスワード"
                        autoComplete="new-password"
                        aria-required="true"
                        aria-describedby="lm-password-hint"
                        className={inputClass}
                    />
                </Field>

                {error && (
                    <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
                        {error}
                    </p>
                )}

                <div className="flex justify-center gap-3 pt-1">
                    <button type="button" onClick={onClose} className={modalCancelBtn} disabled={saving}>
                        キャンセル
                    </button>
                    <button type="submit" className={modalPrimaryBtn} disabled={saving}>
                        {saving ? "登録中…" : "登録する"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

export default function YahooSettingsPage() {
    const { shops, setShops, hasYahoo } = useMallContext();
    const yahooShop = shops.find((s) => s.mall === "yahoo");

    const [storeId, setStoreId] = useState(yahooShop?.mallShopId ?? "");
    const [storeName, setStoreName] = useState(yahooShop?.displayName ?? "");
    const [loginId, setLoginId] = useState("");
    const [credentialsSaved, setCredentialsSaved] = useState(false);

    const [apiConnected, setApiConnected] = useState(false);
    const [apiLinking, setApiLinking] = useState(false);

    // 公開鍵認証（暗号鍵）。未登録だと refresh_token が12時間で失効し自動取得が止まるため、
    // 登録状態と再認可期限（登録済みなら認可から4週間）をステップUIに出す。
    const [publicKeyRegistered, setPublicKeyRegistered] = useState(false);
    const [publicKeyVersionSaved, setPublicKeyVersionSaved] = useState<number | null>(null);
    const [reauthorizeBy, setReauthorizeBy] = useState<string | null>(null);
    const [pubKeyText, setPubKeyText] = useState("");
    const [pubKeyVersion, setPubKeyVersion] = useState("1");
    const [pubKeySaving, setPubKeySaving] = useState(false);
    const [pubKeySaved, setPubKeySaved] = useState(false);

    const [openStep, setOpenStep] = useState<number | null>(1);
    const [activeModal, setActiveModal] = useState<"store" | "login" | null>(null);
    const [disconnecting, setDisconnecting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (yahooShop) {
            setStoreId(yahooShop.mallShopId);
            setStoreName(yahooShop.displayName ?? "");
        }
    }, [yahooShop]);

    // リロード／再マウント後も、サーバー（Secret Manager の有無）から手順3の登録状態を復元する。
    // 値そのものは取得しない。true のときだけ反映し、保存直後のローカル状態を上書きしない。
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiGetJson<{ ok?: boolean; hasCredentials?: boolean }>(
                    "/api/yahoo/credentials/status",
                );
                if (alive && res?.hasCredentials) setCredentialsSaved(true);
            } catch {
                // 取得できなくてもローカル状態のまま（保存操作で更新される）
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    // API 連携（YConnect OAuth）の状態を復元する。値は取得しない（有無だけ）。
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiGetJson<{
                    ok?: boolean;
                    connected?: boolean;
                    reauthorizeBy?: string | null;
                    publicKeyRegistered?: boolean;
                    publicKeyVersion?: number | null;
                }>("/api/auth/yahoo/status");
                if (!alive) return;
                if (res?.connected) setApiConnected(true);
                if (res?.reauthorizeBy) setReauthorizeBy(res.reauthorizeBy);
                if (res?.publicKeyRegistered) {
                    setPublicKeyRegistered(true);
                    setPublicKeyVersionSaved(res.publicKeyVersion ?? null);
                }
            } catch {
                // 取得できなくてもローカル状態のまま
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    const handleApiConnect = async () => {
        setApiLinking(true);
        setError(null);
        try {
            const res = await apiPostJson<{ ok?: boolean; authorizeUrl?: string; message?: string }>(
                "/api/auth/yahoo/start",
                {},
            );
            if (res?.authorizeUrl) {
                window.location.href = res.authorizeUrl;
                return;
            }
            setError(res?.message ?? "API連携をはじめられませんでした。もう一度おためしください。");
        } catch (e) {
            setError("API連携をはじめられませんでした。もう一度おためしください。");
        } finally {
            setApiLinking(false);
        }
    };

    const toggle = (n: number) => setOpenStep((cur) => (cur === n ? null : n));

    const handlePublicKeySave = async () => {
        const trimmedKey = pubKeyText.trim();
        if (!trimmedKey) {
            setError("暗号鍵管理で発行された公開鍵を貼りつけてください");
            return;
        }
        const versionNum = Number(pubKeyVersion);
        if (!Number.isInteger(versionNum) || versionNum < 1) {
            setError("鍵バージョンは1以上の整数で入力してください");
            return;
        }
        setPubKeySaving(true);
        setError(null);
        try {
            const res = await apiPostJson<{ ok?: boolean; message?: string }>(
                "/api/auth/yahoo/public-key",
                { publicKey: trimmedKey, keyVersion: versionNum },
            );
            if (!res?.ok) {
                setError(res?.message ?? "公開鍵を登録できませんでした。もう一度おためしください。");
                return;
            }
            setPublicKeyRegistered(true);
            setPublicKeyVersionSaved(versionNum);
            setPubKeyText("");
            setPubKeySaved(true);
            setTimeout(() => setPubKeySaved(false), 3000);
        } catch (e) {
            setError("公開鍵を登録できませんでした。もう一度おためしください。");
        } finally {
            setPubKeySaving(false);
        }
    };

    // 再認可期限までの残り日数（負なら期限切れ）。公開鍵登録済みの連携でのみ意味を持つ
    const reauthorizeDaysLeft = (() => {
        if (!reauthorizeBy) return null;
        const target = new Date(reauthorizeBy).getTime();
        if (Number.isNaN(target)) return null;
        return Math.floor((target - Date.now()) / (24 * 60 * 60 * 1000));
    })();

    const storeRegistered = Boolean(yahooShop);

    const handleDisconnect = async () => {
        if (!window.confirm("Yahoo!ショッピングの連携をやめますか？")) return;
        setDisconnecting(true);
        setError(null);
        try {
            await apiDeleteJson("/api/company/shops/yahoo");
            setStoreId("");
            setStoreName("");
            setLoginId("");
            setCredentialsSaved(false);
            setShops(shops.filter((s) => s.mall !== "yahoo"));
        } catch (e) {
            setError("連携をやめられませんでした。もう一度おためしください。");
        } finally {
            setDisconnecting(false);
        }
    };

    return (
        <div className="mx-auto max-w-2xl space-y-8 px-4 py-2">
            <header>
                <h1 className="text-xl font-bold text-slate-900">Yahoo!ショッピング 連携設定</h1>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
                    ストアクリエイターProの情報を登録すると、売上・請求明細・広告費のデータを自動で取りこめます。
                </p>
            </header>

            {/* 連携状況（現在の登録内容。ステータスバッジは出さない） */}
            <section
                aria-label="連携状況"
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4"
            >
                <div className="flex items-center gap-3">
                    <span
                        aria-hidden="true"
                        className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-sm font-bold text-slate-500"
                    >
                        Y!
                    </span>
                    <div>
                        <p className="text-sm font-medium text-slate-900">ストアクリエイターPro</p>
                        <p className="mt-0.5 text-xs text-slate-500">
                            {yahooShop
                                ? `店舗ID：${yahooShop.mallShopId}${
                                      yahooShop.displayName && yahooShop.displayName !== yahooShop.mallShopId
                                          ? `（${yahooShop.displayName}）`
                                          : ""
                                  }`
                                : "まだ登録されていません"}
                        </p>
                    </div>
                </div>
                {hasYahoo && (
                    <button
                        type="button"
                        onClick={handleDisconnect}
                        disabled={disconnecting}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-40"
                    >
                        連携をやめる
                    </button>
                )}
            </section>

            {error && (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
                    {error}
                </p>
            )}

            {/* 手順（アコーディオン） */}
            <ol className="rounded-xl border border-slate-200 bg-white px-5">
                <StepAccordion
                    n={1}
                    title="Yahoo! JAPAN IDのセキュリティ設定を確かめる"
                    status={<StatusLine>ログイン方法を確かめてください</StatusLine>}
                    open={openStep === 1}
                    onToggle={() => toggle(1)}
                >
                    <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                        <p>
                            連携するYahoo! JAPAN
                            IDで、自動でのデータの取りこみのさまたげになるログイン方法をオフにします。「ログインとセキュリティ」の画面をひらいて、つぎのとおり設定してください。
                        </p>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <p className="text-xs font-semibold text-slate-500">オフにするもの（未設定・未利用に）</p>
                            <ul className="mt-2 space-y-1.5">
                                <li className="flex gap-2">
                                    <span aria-hidden="true" className="text-slate-400">
                                        ・
                                    </span>
                                    <b className="text-slate-900">パスキー（生体認証）</b>
                                </li>
                                <li className="flex gap-2">
                                    <span aria-hidden="true" className="text-slate-400">
                                        ・
                                    </span>
                                    <b className="text-slate-900">SMS認証</b>
                                </li>
                                <li className="flex gap-2">
                                    <span aria-hidden="true" className="text-slate-400">
                                        ・
                                    </span>
                                    <b className="text-slate-900">ワンタイムパスワード</b>
                                </li>
                            </ul>
                            <p className="mt-4 text-xs font-semibold text-slate-500">そのままにするもの</p>
                            <p className="mt-2">
                                <b className="text-slate-900">パスワードの無効・変更</b>は「
                                <b className="text-slate-900">有効</b>
                                」のままにしてください。Yahooからは「無効」をすすめられますが、無効にするとID・パスワードでログインできず、データを取りこめません。
                            </p>
                        </div>
                        <p className="text-[13px] text-slate-500">
                            すでにオフになっている項目は、そのままで大丈夫です。はじめの1回だけ、弊社で手動のログイン設定をおこないます。
                        </p>
                        <div className="flex justify-center pt-1">
                            <a href={YAHOO_SECURITY_URL} target="_blank" rel="noreferrer" className={stepActionBtn}>
                                ログインとセキュリティをひらく
                                <svg aria-hidden="true" viewBox="0 0 20 20" className="ml-1.5 h-4 w-4" fill="none">
                                    <path
                                        d="M8 5h7v7M15 5l-9 9"
                                        stroke="currentColor"
                                        strokeWidth="1.6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="sr-only">（新しいタブでひらきます）</span>
                            </a>
                        </div>
                    </div>
                </StepAccordion>

                <StepAccordion
                    n={2}
                    title="店舗情報を登録する"
                    status={
                        storeRegistered ? (
                            <StatusLine done>登録済み：{yahooShop?.mallShopId}</StatusLine>
                        ) : (
                            <StatusLine>未登録</StatusLine>
                        )
                    }
                    open={openStep === 2}
                    onToggle={() => toggle(2)}
                >
                    <div className="space-y-3">
                        {storeRegistered ? (
                            <dl className="rounded-lg border border-slate-200 bg-slate-50 p-3.5 text-sm">
                                <div className="flex gap-3">
                                    <dt className="w-24 shrink-0 text-slate-500">店舗ID</dt>
                                    <dd className="font-medium text-slate-900">{yahooShop?.mallShopId}</dd>
                                </div>
                                {storeName.trim() && storeName.trim() !== yahooShop?.mallShopId && (
                                    <div className="mt-1.5 flex gap-3">
                                        <dt className="w-24 shrink-0 text-slate-500">表示名</dt>
                                        <dd className="font-medium text-slate-900">{storeName.trim()}</dd>
                                    </div>
                                )}
                            </dl>
                        ) : (
                            <p className="text-sm leading-relaxed text-slate-600">
                                店舗ID（ストアアカウント）と表示名を登録します。
                            </p>
                        )}
                        <div className="flex justify-center pt-1">
                            <button type="button" onClick={() => setActiveModal("store")} className={stepActionBtn}>
                                {storeRegistered ? "店舗情報をなおす" : "店舗情報を入力する"}
                            </button>
                        </div>
                    </div>
                </StepAccordion>

                <StepAccordion
                    n={3}
                    title="ログイン情報を登録する"
                    status={
                        credentialsSaved ? (
                            <StatusLine done>登録済み</StatusLine>
                        ) : (
                            <StatusLine>未登録</StatusLine>
                        )
                    }
                    open={openStep === 3}
                    onToggle={() => toggle(3)}
                >
                    <div className="space-y-3">
                        <p className="text-sm leading-relaxed text-slate-600">
                            Yahoo! JAPAN IDとパスワードを登録します。パスワードは暗号化して大切に保管します。
                        </p>
                        {!storeRegistered && (
                            <p className="text-sm text-slate-500">さきに「店舗情報」を登録してください。</p>
                        )}
                        {credentialsSaved && (
                            <p
                                role="status"
                                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800"
                            >
                                連携情報を登録しました。はじめの1回だけ、弊社でログイン設定をおこないます。
                            </p>
                        )}
                        <div className="flex justify-center pt-1">
                            <button
                                type="button"
                                onClick={() => setActiveModal("login")}
                                disabled={!storeRegistered}
                                className={stepActionBtn}
                            >
                                {credentialsSaved ? "ログイン情報をなおす" : "ログイン情報を入力する"}
                            </button>
                        </div>
                    </div>
                </StepAccordion>

                <StepAccordion
                    n={4}
                    title="Yahoo!ショッピング APIと連携する"
                    status={apiConnected ? <StatusLine done>連携済み</StatusLine> : <StatusLine>未連携</StatusLine>}
                    open={openStep === 4}
                    onToggle={() => toggle(4)}
                >
                    <div className="space-y-3">
                        <p className="text-sm leading-relaxed text-slate-600">
                            売上・注文件数・クーポン値引額を、Yahoo!ショッピングのAPIから自動で取りこみます。ボタンを押すとYahooの同意画面がひらくので、そのまま許可してください。
                        </p>
                        {!storeRegistered && (
                            <p className="text-sm text-slate-500">さきに「店舗情報」を登録してください。</p>
                        )}
                        {apiConnected && (
                            <p
                                role="status"
                                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800"
                            >
                                APIと連携できました。データの取りこみがはじまります。
                            </p>
                        )}
                        {apiConnected && reauthorizeDaysLeft !== null && (
                            <p
                                role="status"
                                className={[
                                    "rounded-lg border px-3.5 py-2.5 text-sm",
                                    reauthorizeDaysLeft <= 3
                                        ? "border-red-200 bg-red-50 text-red-700"
                                        : reauthorizeDaysLeft <= 7
                                          ? "border-amber-200 bg-amber-50 text-amber-800"
                                          : "border-slate-200 bg-slate-50 text-slate-600",
                                ].join(" ")}
                            >
                                {reauthorizeDaysLeft >= 0
                                    ? `Yahoo!のきまりで、約4週間ごとに連携のしなおしが必要です（あと${reauthorizeDaysLeft}日）。期限がきたら「APIと連携しなおす」を押してください。`
                                    : "連携の期限が切れています。「APIと連携しなおす」を押して、もう一度許可してください。"}
                            </p>
                        )}
                        <div className="flex justify-center pt-1">
                            <button
                                type="button"
                                onClick={handleApiConnect}
                                disabled={!storeRegistered || apiLinking}
                                className={stepActionBtn}
                            >
                                {apiLinking ? "ひらいています…" : apiConnected ? "APIと連携しなおす" : "APIと連携する"}
                            </button>
                        </div>
                    </div>
                </StepAccordion>

                <StepAccordion
                    n={5}
                    title="暗号鍵（公開鍵）を登録する"
                    status={
                        publicKeyRegistered ? (
                            <StatusLine done>
                                登録済み{publicKeyVersionSaved !== null ? `（バージョン${publicKeyVersionSaved}）` : ""}
                            </StatusLine>
                        ) : (
                            <StatusLine>未登録</StatusLine>
                        )
                    }
                    open={openStep === 5}
                    onToggle={() => toggle(5)}
                >
                    <div className="space-y-3">
                        <p className="text-sm leading-relaxed text-slate-600">
                            ストアクリエイターProの「設定 → 暗号鍵管理」で鍵を発行し、表示された公開鍵と鍵バージョンを登録します。
                            この登録がないと、Yahoo!のきまりでAPI連携が12時間で切れてしまい、自動での取りこみが続けられません。
                            登録すると連携のしなおしは約4週間ごとになります。
                        </p>
                        {!storeRegistered && (
                            <p className="text-sm text-slate-500">さきに「店舗情報」を登録してください。</p>
                        )}
                        {pubKeySaved && (
                            <p
                                role="status"
                                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800"
                            >
                                公開鍵を登録しました。
                            </p>
                        )}
                        <div>
                            <label htmlFor="ysc-public-key" className="mb-1 block text-sm font-medium text-slate-700">
                                公開鍵
                            </label>
                            <textarea
                                id="ysc-public-key"
                                value={pubKeyText}
                                onChange={(e) => setPubKeyText(e.target.value)}
                                rows={5}
                                placeholder="-----BEGIN PUBLIC KEY----- から貼りつけ（ヘッダー行なしでもかまいません）"
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                            />
                        </div>
                        <div>
                            <label htmlFor="ysc-key-version" className="mb-1 block text-sm font-medium text-slate-700">
                                鍵バージョン
                            </label>
                            <input
                                id="ysc-key-version"
                                type="number"
                                min={1}
                                value={pubKeyVersion}
                                onChange={(e) => setPubKeyVersion(e.target.value)}
                                className="w-32 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                            />
                            <p className="mt-1 text-xs text-slate-400">暗号鍵管理の画面に表示されるバージョン番号です</p>
                        </div>
                        <div className="flex justify-center pt-1">
                            <button
                                type="button"
                                onClick={handlePublicKeySave}
                                disabled={!storeRegistered || pubKeySaving || !pubKeyText.trim()}
                                className={stepActionBtn}
                            >
                                {pubKeySaving ? "登録しています…" : publicKeyRegistered ? "公開鍵をなおす" : "公開鍵を登録する"}
                            </button>
                        </div>
                    </div>
                </StepAccordion>
            </ol>

            <StoreModal
                open={activeModal === "store"}
                initialStoreId={storeId}
                initialStoreName={storeName}
                onClose={() => setActiveModal(null)}
                onSaved={(s, name) => {
                    const newShop = shopRefFromStoreId(s, name);
                    setShops([...shops.filter((sh) => sh.mall !== "yahoo"), newShop]);
                    setStoreId(s);
                    setStoreName(name);
                    setActiveModal(null);
                    setOpenStep(3);
                }}
            />

            <LoginModal
                open={activeModal === "login"}
                initialLoginId={loginId}
                onClose={() => setActiveModal(null)}
                onSaved={(l) => {
                    setLoginId(l);
                    setCredentialsSaved(true);
                    setActiveModal(null);
                }}
            />
        </div>
    );
}
